import { useEffect, useMemo, useRef, useState } from "react";
import { FAMILIES } from "@/config/families";

/**
 * FamilyRaceLivePreview
 * ---------------------------------------------------------------
 * A COMPACT, NON-INTERACTIVE promotional preview of the Family
 * Race board rendered inside the Family Race card banner area.
 *
 * Reuses:
 *   • FAMILIES config (slug / name / color / emblem)
 *   • The visual language of RaceLane (checkerboard slots +
 *     glowing radial puck + neon accent per family)
 *
 * Isolation guarantees:
 *   • no network calls, no WebSocket, no MongoDB writes
 *   • no admin controls
 *   • never reads/writes real race state
 *   • the animation is fully deterministic (fixed step schedule
 *     + fixed starting positions), so it looks the same on
 *     every device
 *
 * Perf:
 *   • pauses when the card is off-screen (IntersectionObserver)
 *   • honours prefers-reduced-motion (frozen frame + no interval)
 *   • pure CSS transitions on the puck; no animation library
 * ---------------------------------------------------------------
 */

const SLOT_COUNT = 12;                // compact lane (real board uses 24)
const CHECKPOINT_EVERY = 3;           // 4 checkpoints total
const STEP_MS = 650;                  // one puck advance per tick
// Deterministic starting offsets (per FAMILIES index). Chosen so the
// mini-board looks like a real snapshot in-play, not a fresh start.
const START_POSITIONS = [3, 2, 4, 1, 5, 2, 3, 4, 2, 3];
// Deterministic advance sequence: which lane advances at each tick.
// One family at a time, staggered feel.
const ADVANCE_SEQUENCE = [3, 7, 0, 5, 9, 2, 6, 1, 8, 4, 5, 3, 7];

function buildInitial() {
  return FAMILIES.map((_, i) => START_POSITIONS[i] ?? 0);
}

export function FamilyRaceLivePreview({ active }) {
  const [positions, setPositions] = useState(buildInitial);
  const tickRef = useRef(0);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const [inView, setInView] = useState(true);

  // Off-screen pause via IntersectionObserver.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Reduced motion — freeze the initial frame, no interval.
  const reducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    // Only tick while the preview state is active AND visible AND motion allowed.
    if (!active || !inView || reducedMotion) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return undefined;
    }

    // Reset positions to the deterministic start every time preview activates
    // so the animation is identical across cycles.
    tickRef.current = 0;
    setPositions(buildInitial());

    timerRef.current = window.setInterval(() => {
      const idx = ADVANCE_SEQUENCE[tickRef.current % ADVANCE_SEQUENCE.length];
      tickRef.current += 1;
      setPositions((prev) => {
        const next = prev.slice();
        next[idx] = Math.min(SLOT_COUNT, next[idx] + 1);
        return next;
      });
    }, STEP_MS);

    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, inView, reducedMotion]);

  return (
    <div
      ref={containerRef}
      data-testid="family-race-live-preview"
      className="fr-live-preview"
      aria-hidden="true"
    >
      <div className="fr-live-preview__grid">
        {FAMILIES.map((fam, i) => (
          <MiniLane
            key={fam.slug}
            family={fam}
            position={positions[i] ?? 0}
          />
        ))}
      </div>
      {/* Ambient scan sweep — subtle, matches Prophets sci-fi language */}
      <span aria-hidden="true" className="fr-live-preview__sweep" />
    </div>
  );
}

function MiniLane({ family, position }) {
  const color = family.color;
  // Build slot list top-to-bottom (index SLOT_COUNT..1). Position 0 = start (bottom).
  const slots = [];
  for (let i = SLOT_COUNT; i >= 1; i -= 1) {
    const isCheckpoint = i % CHECKPOINT_EVERY === 0;
    const active = i <= position;
    slots.push({ i, isCheckpoint, active });
  }
  // Puck vertical position: 0 = bottom (start), SLOT_COUNT = top (finish).
  const pct = (position / SLOT_COUNT) * 100;

  return (
    <div className="fr-live-preview__lane">
      <div className="fr-live-preview__track">
        {slots.map((s) => (
          <div
            key={s.i}
            className={`fr-live-preview__slot ${s.isCheckpoint ? "is-checkpoint" : "is-dash"} ${s.active ? "is-active" : ""}`}
            style={{
              "--fr-color": color,
              background: s.isCheckpoint ? "transparent" : (s.active ? color : `${color}44`),
              borderColor: s.isCheckpoint ? (s.active ? color : `${color}66`) : "transparent",
              boxShadow: s.active
                ? (s.isCheckpoint
                    ? `0 0 8px ${color}, inset 0 0 4px ${color}80`
                    : `0 0 6px ${color}`)
                : "none",
            }}
          />
        ))}
        {/* Glowing puck — the only element that moves smoothly */}
        <div
          className="fr-live-preview__puck"
          style={{
            bottom: `calc(${pct}% )`,
            background: `radial-gradient(circle, #ffffff 0%, ${color} 55%, ${color} 100%)`,
            boxShadow: `0 0 10px ${color}, 0 0 20px ${color}88`,
            border: `1.5px solid ${color}`,
          }}
        />
      </div>
      {/* Family emblem below the lane, tiny */}
      <div
        className="fr-live-preview__emblem"
        style={{
          borderColor: `${color}55`,
          boxShadow: `0 0 8px ${color}55, inset 0 0 6px ${color}22`,
        }}
      >
        <img
          src={family.emblem}
          alt=""
          draggable={false}
          loading="lazy"
        />
      </div>
    </div>
  );
}
